import { Link, Navigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { apiFetch } from '@/lib/api.js';
import { useSession } from '@/auth/useSession.js';
import { useMe } from '@/app/office-context.jsx';

/**
 * Authenticated landing : bounce to the first office ; with no office, to the
 * pending invitations (0068) when there are some, else onboarding.
 */
function AuthenticatedRedirect() {
  const { t } = useTranslation('common');
  const me = useMe();
  const noOffice = me.status === 'success' && !me.data.offices?.[0];
  const invitations = useQuery({
    queryKey: ['me', 'invitations'],
    queryFn: () => apiFetch('/api/me/invitations'),
    enabled: noOffice,
    retry: false,
  });

  if (me.status === 'pending' || (noOffice && invitations.isPending)) {
    return <div role="status" aria-live="polite">{t('loading')}</div>;
  }
  if (me.status === 'error') {
    return <p role="alert">{t('error.generic')}</p>;
  }
  const firstOffice = me.data.offices?.[0];
  if (!firstOffice && invitations.data?.invitations?.length > 0) return <Navigate to="/invitations" replace />;
  return <Navigate to={firstOffice ? `/offices/${firstOffice.id}/home` : '/onboarding'} replace />;
}

export default function Index() {
  const { t } = useTranslation('common');
  const { status } = useSession();

  if (status === 'loading') {
    return <div role="status" aria-live="polite">{t('loading')}</div>;
  }
  if (status === 'authenticated') {
    return <AuthenticatedRedirect />;
  }

  return (
    <section className="flex flex-1 items-center justify-center px-6 py-12">
      <div className="w-full max-w-md text-center">
        <h1 className="text-3xl font-semibold tracking-tight text-slate-900 dark:text-slate-100">{t('app.name')}</h1>
        <p className="mt-3 text-base text-slate-500 dark:text-slate-400">{t('app.tagline')}</p>
        <div className="mt-8 flex flex-col items-center gap-3">
          <Link
            to="/start"
            className="inline-flex items-center justify-center rounded-md bg-slate-900 px-5 py-2.5 text-sm font-medium text-white hover:bg-slate-800 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-slate-900"
          >
            {t('nova:hero.title', 'Build your AI team')}
          </Link>
          <Link to="/login" className="text-sm text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200">
            {t('nav.login')}
          </Link>
        </div>
      </div>
    </section>
  );
}
